<template>
  <div class="user-modal" :class="{ showing }">
    <div class="modal-body">
      <i v-show="!saving" class="material-icons close-btn" @click="close()"
        >clear</i
      >

      <div class="top-panel">
        <h4 v-show="!saving">{{ mode }} User</h4>
        <h4 class="saving-header" v-show="saving">Saving Changes</h4>
      </div>

      <div v-show="!saving" class="middle-panel" ref="scrollzone">
        <h6>User details</h6>

        <material-input
          v-model="user.firstName"
          type="text"
          :label="`First Name`"
          :required="true"
          name="FirstName"
        />
        <material-input
          v-model="user.lastName"
          type="text"
          :label="`Last Name`"
          :required="true"
          name="LastName"
        />

        <dropdown
          :options="jobTitleOptions"
          :value="user.jobTitle"
          :onChange="val => (user.jobTitle = val)"
          placeholder="Job Title"
        />

        <dropdown
          :options="roleOptions"
          :value="user.role"
          :onChange="changeRole"
          placeholder="Role"
          :disabled="mode === 'Edit'"
        />

        <dropdown
          :options="orgOptions"
          :value="user.organizationId"
          :onChange="val => (user.organizationId = val)"
          placeholder="Organisation"
          v-if="app_user.role === 'SuperAdmin'"
          :disabled="mode === 'Edit'"
        />

        <b-form-checkbox
          v-model="user.inviteUser"
          class="checkbox"
          @change="invite"
          :disabled="mode === 'Edit'"
          >This user can login to Acada</b-form-checkbox
        >

        <material-input
          v-show="user.inviteUser"
          v-model="user.email"
          type="text"
          :label="`Email`"
          :required="true"
          name="Email"
          :disabled="mode === 'Edit'"
        />
      </div>

      <div v-show="saving" class="middle-panel saving">
        <img src="~@/assets/icons/spinner.svg" />
        <p>Saving</p>
      </div>

      <div class="bottom-panel" v-show="!saving">
        <a
          class="button delete-button"
          v-if="userId && userId !== -1"
          @click="deleteUser()"
          >Delete</a
        >
        <a class="button secondary-button" @click="close()">Cancel</a>
        <a class="button" @click="save()" :disabled="formDisabled">Save</a>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialInput from './MaterialInput.vue';
import Dropdown from './Dropdown';

export default {
  name: 'UserModal',
  data() {
    return {
      userSchema: {
        firstName: '',
        lastName: '',
        email: null,
        jobTitle: null,
        role: 'User',
        inviteUser: false,
        organizationId: this.orgId,
      },
      saving: false,
      user: {},
      jobTitleOptions: [
        'Legal',
        'Finance',
        'Business',
        'Procurement',
        'Other',
      ].map(m => {
        return { value: m, text: m };
      }),
      roleOptions: ['Stakeholder', 'User', 'Admin', 'SuperAdmin'].map(m => {
        return { value: m, text: m };
      }),
    };
  },
  props: {
    showing: Boolean,
    mode: String,
    toggleModal: Function,
    onSave: Function,
    onDelete: Function,
    userId: [Number, String],
    orgId: [Number, String],
  },
  methods: {
    changeTab(index) {
      this.tabIndex = index;
    },
    async save() {
      this.saving = true;
      try {
        await this.onSave(this.user);
        this.close(false);
      } catch (e) {
        this.saving = false;
        alert(e);
      }
    },
    async deleteUser() {
      try {
        const options = {
          html: false,
          okText: 'Delete',
          cancelText: 'Cancel',
          customClass: 'acada-confirm',
          animation: 'fade',
        };

        const message = {
          title: 'Delete user',
          body:
            'Are you sure you want to delete this user? This action cannot be undone',
        };

        await this.$dialog.confirm(message, options);

        await this.onDelete(this.user);
        this.close(false);
      } catch (e) {
        // they clicked cancel, so don't close the modal
      }
    },
    async close() {
      this.reset();
      this.toggleModal(false);
    },
    changeRole(val) {
      this.user.role = val;
      if (val !== 'User') {
        this.user.inviteUser = true;
      }
    },
    invite(val) {
      if (!val) {
        this.user.role = 'User';
      }
    },
    reset() {
      this.saving = false;
      this.user = { ...this.userSchema };
    },
  },
  computed: {
    formDisabled() {
      const regex = RegExp(
        /^[a-zA-Z0-9_+!?$#%&=-]([\.-]?[a-zA-Z0-9_+!?$#%&=-])*@\w+([\.-]?\w+)*(\.\w{2,4})+$/
      );
      const validEmail = this.user.inviteUser
        ? regex.test(this.user.email)
        : true;
      return (
        !this.user.firstName ||
        !this.user.lastName ||
        !this.user.jobTitle ||
        !validEmail
      );
    },
    orgs() {
      return this.$store.getters.all_orgs;
    },
    orgOptions() {
      return [...this.orgs]
        .sort((a, b) => {
          if (a.orgName < b.orgName) return -1;
          return 1;
        })
        .map(org => {
          return { value: org.id, text: org.orgName };
        });
    },
    app_user() {
      return this.$store.getters.app_user;
    },
  },
  watch: {
    userId(newValue) {
      if (newValue === -1) {
        this.reset();
      } else if (newValue) {
        const user = this.$store.getters.orgUsers[newValue];
        this.user = JSON.parse(JSON.stringify(user));
        this.user.inviteUser = this.user.userType === 'Cognito';
      }
    },
    orgId(newValue) {
      this.userSchema.organizationId = newValue;
      this.user.organizationId = newValue;
    },
  },
  components: {
    'material-input': MaterialInput,
    dropdown: Dropdown,
  },
  created() {
    this.reset();

    if (!this.orgs.length) {
      this.$store.dispatch('getAllOrgs');
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/_styles.scss';

.user-modal {
  opacity: 0;
  visibility: hidden;
  left: -10000px;
  position: absolute;
  z-index: 2000;
  top: 0;
  width: 0;
  height: 0;
  transition: opacity 0.5s;

  .modal-body {
    width: calc(100% - 100px);
    max-width: 740px;
    height: calc(100% - 70px);
    position: fixed;
    z-index: 20;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(calc(-50% - 100px)) translateZ(0);
    background: white;
    will-change: transform;
    transition: transform 500ms;
    box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: 0;

    .close-btn {
      position: absolute;
      top: 0px;
      right: 0px;
      padding: 10px;
      color: #acb6c1;
      cursor: pointer;
      user-select: none;
      font-size: 20px;
    }

    .top-panel {
      width: 100%;
      height: 126px;
      border-bottom: 1px solid #e2e5ec;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-direction: column;

      h4 {
        font-size: 24px;
        margin-top: 35px;
      }

      h4.saving-header {
        margin-top: 60px;
      }

      .hidden {
        pointer-events: none;
        visibility: hidden;
      }
    }

    .middle-panel {
      width: 100%;
      flex: 1;
      overflow-x: hidden;
      overflow-y: auto;
      background: $bg-color;
      padding: 30px;
      opacity: 0;
      transition: opacity 0.1s;
      position: relative;

      > input,
      > label {
        max-width: 460px;
        margin: 0 auto 25px;
        font-size: 14px;
        display: block;
        width: 100%;
      }

      > label {
        font-size: 14px;
        margin-bottom: 5px;
      }

      .add-btn {
        width: 460px;
        margin: 12px auto;
        display: block;
        font-size: 14px;
        opacity: 0.5;
        color: $sidebar-color;
        cursor: pointer;

        i {
          margin-right: 3px;
          vertical-align: middle;
          position: relative;
          top: -1px;
          font-size: 18px;
        }

        &:hover {
          opacity: 0.7;
        }
      }

      > div {
        max-width: 460px;
        display: block;
        margin: 0 auto;
      }

      ::v-deep h6 {
        margin: 20px auto;
        max-width: 460px;

        &:not(:first-child) {
          margin-top: 60px;
        }

        &.spaced {
          margin-top: 40px;
        }
      }

      &.saving {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        flex-wrap: nowrap;

        img {
          width: 40px;
          height: 40px;
          animation: spin 1.5s linear infinite;
        }

        p {
          font-weight: 400;
          font-size: 14px;
          color: #565b64;
          margin: 20px 0 0;
        }
      }
    }

    .bottom-panel {
      width: 100%;
      height: 90px;
      border-top: 1px solid #e2e5ec;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-direction: row;
      padding: 0 40px;

      .button {
        width: 130px;
        min-width: 130px;
        margin: 0;
        margin-left: 10px;

        &[disabled] {
          pointer-events: none;
          opacity: 0.5;
        }
      }
    }
  }

  &.showing {
    background: rgba(38, 41, 47, 0.6);
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 1;
    visibility: visible;

    .modal-body {
      transform: translateX(-50%) translateY(-50%) translateZ(0);
    }

    .middle-panel {
      opacity: 1;
    }
  }
}

::v-deep .small-caps {
  text-transform: uppercase;
  font-size: 10px;
  color: #929da9;
  font-weight: 500;
  margin-top: 20px;

  &:first-child {
    margin-top: 0;
  }
}

::v-deep .grid {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: flex-start;

  > .datepicker {
    width: 215px;
    margin-bottom: 30px;
    margin-top: 0px;

    label {
      text-transform: uppercase;
      font-size: 10px;
      display: block;
      font-weight: 500;
      color: #929da9;
    }
  }
}

::v-deep .uppercase input {
  text-transform: uppercase;
}

.material-input,
.material-select {
  margin-bottom: 30px !important;
}

.checkbox {
  margin-bottom: 24px !important;
}

@keyframes spin {
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
</style>
